/**
 * Stage controller (04 Stage and chrome, 09 §3, §8, §9).
 *
 * - Measures the app area in **device** px (`devicePixelContentBoxSize`, DPR listener) and runs
 *   `resolveScale` → `f`, stage size, offset, portrait / rotate / too-small flags. A portrait window
 *   also reads the OS text factor (`measureTextFactor`) and `(pointer: coarse)` on every measure.
 * - The first layout applies at once. Later changes (window drag, fullscreen, monitor/zoom
 *   change) CSS-stretch the previous frame immediately and apply the real relayout
 *   after a 150 ms debounce (09 §9: "the previous frame is CSS-stretched; the relayout happens
 *   when the drag pauses"). An unchanged (f, stage, offset, device) result does nothing.
 * - Pointer mapping CSS px → device px → stage px (09 §7). It uses the stage element's live CSS
 *   box, so it is also correct while the previous frame is CSS-stretched.
 * - Fullscreen API toggle (`F`, ⛶); entering or leaving fullscreen is a normal resize.
 */
import type { ScaleState, Size } from '../core/api.ts';
import { resolveScale, stageDeviceSize, type ResolvedScale } from '../core/layout/scale.ts';

/**
 * Observe an element's content box in **device** px (09 §6, §9).
 *
 * - Prefers `devicePixelContentBoxSize` (exact, no rounding drift); falls back to
 *   `contentRect × devicePixelRatio` where the browser does not report it.
 * - Listens to `matchMedia('(resolution: <dpr>dppx)')` so a DPR change (browser zoom, moving the
 *   window to another monitor) re-measures even when the CSS size is unchanged. Re-observing makes
 *   the ResizeObserver deliver a fresh entry, so the device-pixel box is still preferred.
 * - The callback fires only when the device size or the DPR changes (CSS placement depends on
 *   both). Returns a disposer.
 */
export function observeDevicePixelSize(el: Element, onSize: (size: Size, dpr: number) => void): () => void {
  let last = { w: -1, h: -1, dpr: -1 };
  const ro = new ResizeObserver((entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;
    const size = deviceSizeOf(entry);
    const dpr = window.devicePixelRatio || 1;
    if (size.w === last.w && size.h === last.h && dpr === last.dpr) return;
    last = { ...size, dpr };
    onSize(size, dpr);
  });
  const observe = (): void => {
    try {
      ro.observe(el, { box: 'device-pixel-content-box' });
    } catch {
      ro.observe(el, { box: 'content-box' });
    }
  };
  observe();

  let mql: MediaQueryList | null = null;
  const onDprChange = (): void => {
    armDprListener(); // the query is for one specific dpr: re-arm for the new value
    ro.unobserve(el);
    observe(); // observe() always delivers an initial entry with fresh sizes
  };
  const armDprListener = (): void => {
    mql?.removeEventListener('change', onDprChange);
    mql = window.matchMedia(`(resolution: ${String(window.devicePixelRatio)}dppx)`);
    mql.addEventListener('change', onDprChange);
  };
  armDprListener();

  return () => {
    mql?.removeEventListener('change', onDprChange);
    ro.disconnect();
  };
}

function deviceSizeOf(entry: ResizeObserverEntry): Size {
  const dpcb = entry.devicePixelContentBoxSize as ReadonlyArray<ResizeObserverSize> | undefined;
  const box = dpcb?.[0];
  if (box) return { w: box.inlineSize, h: box.blockSize };
  const dpr = window.devicePixelRatio || 1;
  return {
    w: Math.round(entry.contentRect.width * dpr),
    h: Math.round(entry.contentRect.height * dpr),
  };
}

/** OS text factor (portrait plan §S, P3): the size of a probe relative to the platform default
 *  (iOS `font: -apple-system-body` / 17, elsewhere `1rem` / 16), clamped to 0.85–1.6. */
export function textFactorOf(px: number, apple: boolean): number {
  const k = px / (apple ? 17 : 16);
  return Number.isFinite(k) && k > 0 ? Math.min(1.6, Math.max(0.85, k)) : 1;
}

/** Measure the OS text factor with a hidden probe (the `-apple-system-body` keyword only parses on WebKit). */
export function measureTextFactor(): number {
  const el = document.createElement('div');
  el.style.cssText = 'position:absolute;visibility:hidden;pointer-events:none';
  el.style.font = '-apple-system-body';
  const apple = el.style.font !== '';
  if (!apple) el.style.fontSize = '1rem';
  document.body.appendChild(el);
  const px = parseFloat(getComputedStyle(el).fontSize);
  el.remove();
  return textFactorOf(px, apple);
}

/** Relayout debounce after the first layout (09 §9). */
export const RELAYOUT_DEBOUNCE_MS = 150;

/** One applied stage layout: the resolved scale plus the DPR it was placed with. */
export interface StageLayout {
  resolved: ResolvedScale;
  scale: ScaleState;
  dpr: number;
}

export interface StageControllerOptions {
  /** the whole device area (#app) */
  appEl: HTMLElement;
  /** the resolved stage rect inside it (#stage) */
  stageEl: HTMLElement;
  /** current GL render scale r (governor) */
  renderScale(): number;
  /** called with every applied layout (first one synchronously from the first measurement) */
  onLayout(l: StageLayout, prev: StageLayout | null): void;
  debounceMs?: number;
}

/** Build the shared `ScaleState` (api.ts) from a resolved scale and the render scale. */
export function scaleStateOf(r: ResolvedScale, renderScale: number): ScaleState {
  const k = r.f;
  const rr = renderScale > 0 ? renderScale : 1;
  return {
    k,
    stage: { w: r.stage.w, h: r.stage.h },
    device: stageDeviceSize(r),
    r: rr,
    pxScale: k / rr,
  };
}

function sameResolved(a: ResolvedScale, b: ResolvedScale): boolean {
  return (
    Math.abs(a.f - b.f) < 1e-9 &&
    a.stage.w === b.stage.w && a.stage.h === b.stage.h &&
    a.offset.x === b.offset.x && a.offset.y === b.offset.y &&
    a.device.w === b.device.w && a.device.h === b.device.h
  );
}

export class StageController {
  private current: StageLayout | null = null;
  private pending: { resolved: ResolvedScale; dpr: number } | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private lastDevice: Size | null = null;
  private lastDpr = 1;
  private dispose: (() => void) | null = null;
  private readonly debounceMs: number;
  /** wall time (performance.now) of the last applied relayout, for tests and the debug overlay */
  lastRelayoutMs = 0;
  relayouts = 0;
  /** the OS text factor of the last measure (1 in landscape) */
  textK = 1;

  constructor(private readonly o: StageControllerOptions) {
    this.debounceMs = o.debounceMs ?? RELAYOUT_DEBOUNCE_MS;
  }

  get layout(): StageLayout | null {
    return this.current;
  }

  start(): void {
    this.dispose?.(); // idempotent: a Retry re-entering boot must dispose the previous observer,
    // never stack a second one (both would fire onLayout on every resize)
    this.dispose = observeDevicePixelSize(this.o.appEl, (size, dpr) => {
      this.lastDevice = size;
      this.lastDpr = dpr;
      this.measure(false);
    });
    document.addEventListener('fullscreenchange', this.onFullscreen);
  }

  stop(): void {
    this.dispose?.();
    this.dispose = null;
    if (this.timer) clearTimeout(this.timer);
    document.removeEventListener('fullscreenchange', this.onFullscreen);
  }

  /** Re-apply the current layout with a new render scale r (governor); no debounce, no CSS move. */
  setRenderScale(): void {
    const cur = this.current;
    if (!cur) return;
    const next: StageLayout = { resolved: cur.resolved, scale: scaleStateOf(cur.resolved, this.o.renderScale()), dpr: cur.dpr };
    if (next.scale.r === cur.scale.r) return;
    this.current = next;
    this.o.onLayout(next, cur);
  }

  private measure(immediate: boolean): void {
    const dev = this.lastDevice;
    if (!dev) return;
    const dpr = this.lastDpr;
    // the text factor is portrait-only (landscape stays at k = 1: byte identity)
    const k = dev.w < dev.h ? measureTextFactor() : 1;
    this.textK = k;
    const r = resolveScale(dev.w, dev.h, dpr, k, window.matchMedia('(pointer: coarse)').matches);
    if (!r) return; // degenerate (minimised): keep the previous layout
    const cur = this.current;
    if (cur && sameResolved(cur.resolved, r) && cur.dpr === dpr) {
      // back to the applied layout before the debounce fired: undo any CSS stretch
      if (this.pending) {
        this.pending = null;
        if (this.timer) clearTimeout(this.timer);
        this.timer = null;
        this.placeStage(cur.resolved, cur.dpr);
      }
      return;
    }
    if (!cur || immediate) {
      this.apply(r, dpr);
      return;
    }
    // live change: stretch the previous frame into the new stage box, relayout when it settles
    this.pending = { resolved: r, dpr };
    this.placeStage(r, dpr);
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.timer = null;
      const p = this.pending;
      this.pending = null;
      if (p) this.apply(p.resolved, p.dpr);
    }, this.debounceMs);
  }

  private apply(r: ResolvedScale, dpr: number): void {
    const prev = this.current;
    const next: StageLayout = { resolved: r, scale: scaleStateOf(r, this.o.renderScale()), dpr };
    this.current = next;
    this.placeStage(r, dpr);
    const el = this.o.stageEl;
    el.dataset.scale = `f${r.f.toFixed(3)}`;
    el.dataset.stage = `${String(r.stage.w)}x${String(r.stage.h)}`;
    el.dataset.textk = this.textK.toFixed(3);
    this.relayouts++;
    this.o.onLayout(next, prev);
    this.lastRelayoutMs = performance.now();
  }

  /** Place #stage at the resolved stage rect (device px → CSS px). */
  private placeStage(r: ResolvedScale, dpr: number): void {
    const dev = stageDeviceSize(r);
    const s = this.o.stageEl.style;
    s.left = `${String(r.offset.x / dpr)}px`;
    s.top = `${String(r.offset.y / dpr)}px`;
    s.width = `${String(dev.w / dpr)}px`;
    s.height = `${String(dev.h / dpr)}px`;
  }

  /**
   * CSS px (client coordinates) → stage px (09 §7). Uses the live CSS box of #stage: in steady
   * state that is exactly (client − left) · dpr / k.
   */
  toStage(clientX: number, clientY: number, out: [number, number]): [number, number] {
    const cur = this.current;
    if (!cur) {
      out[0] = clientX;
      out[1] = clientY;
      return out;
    }
    const box = this.o.stageEl.getBoundingClientRect();
    const st = cur.resolved.stage;
    out[0] = box.width > 0 ? ((clientX - box.left) / box.width) * st.w : 0;
    out[1] = box.height > 0 ? ((clientY - box.top) / box.height) * st.h : 0;
    return out;
  }

  // ───────────────────────────── fullscreen ─────────────────────────────

  get isFullscreen(): boolean {
    return document.fullscreenElement !== null;
  }

  async toggleFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
    } catch {
      // not allowed (no user gesture, iframe policy): ignore
    }
  }

  private readonly onFullscreen = (): void => {
    document.documentElement.dataset.fullscreen = document.fullscreenElement ? '1' : '0';
  };
}
